import React, { useState } from 'react';
import { X, Info, Tag as TagIcon } from 'lucide-react';
import { useCreateNote, type NoteCategory, type NotePriority } from '../../../services/queries/noteQueries';
import { StandardButton, StandardInput, StandardSelect, StandardTextarea, StandardCheckbox } from '../../standard';

interface CreateNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateNoteModal: React.FC<CreateNoteModalProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general' as NoteCategory,
    priority: 'medium' as NotePriority,
    tags: [] as string[],
    isPinned: false,
    isArchived: false,
    attachments: []
  });

  const [tagInput, setTagInput] = useState('');

  const createNoteMutation = useCreateNote();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await createNoteMutation.mutateAsync({
        ...formData,
        relatedTo: undefined
      });

      onClose();
      resetForm();
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general',
      priority: 'medium',
      tags: [],
      isPinned: false,
      isArchived: false,
      attachments: []
    });
    setTagInput('');
  };

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Note</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Create a new note with rich text and tags
            </p>
          </div>
          <button
            onClick={() => {
              onClose();
              resetForm();
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>

              <div className="space-y-4">
                <StandardInput
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Meeting Notes - Q1 Planning"
                  required
                />

                <div className="grid grid-cols-2 gap-4">
                  <StandardSelect
                    label="Category"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as NoteCategory }))}
                    required
                    options={[
                      { value: 'general', label: 'General' },
                      { value: 'meeting', label: 'Meeting' },
                      { value: 'task', label: 'Task' },
                      { value: 'idea', label: 'Idea' },
                      { value: 'issue', label: 'Issue' },
                      { value: 'decision', label: 'Decision' }
                    ]}
                  />

                  <StandardSelect
                    label="Priority"
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as NotePriority }))}
                    required
                    options={[
                      { value: 'low', label: 'Low' },
                      { value: 'medium', label: 'Medium' },
                      { value: 'high', label: 'High' }
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Note Content</h3>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    <p className="font-semibold mb-1">Rich Text Support:</p>
                    <p>You can use HTML tags for formatting: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;</p>
                  </div>
                </div>
              </div>

              <StandardTextarea
                label="Content"
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your note content here... You can use HTML tags for formatting."
                rows={12}
                required
                className="font-mono text-sm"
              />
            </div>

            {/* Tags */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tags</h3>

              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <StandardInput
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyDown={handleTagKeyDown}
                      placeholder="Add tags (press Enter)"
                      leftIcon={<TagIcon className="w-4 h-4" />}
                    />
                  </div>
                  <StandardButton
                    type="button"
                    variant="primary"
                    onClick={addTag}
                  >
                    Add Tag
                  </StandardButton>
                </div>

                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map(tag => (
                      <span
                        key={tag}
                        className="px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 rounded-full flex items-center gap-2 text-sm"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeTag(tag)}
                          className="hover:text-purple-900 dark:hover:text-purple-200"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Options */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Options</h3>

              <div className="space-y-2">
                <StandardCheckbox
                  label="Pin this note to the top"
                  checked={formData.isPinned}
                  onChange={(checked) => setFormData(prev => ({ ...prev, isPinned: checked }))}
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <StandardButton
            type="button"
            variant="ghost"
            onClick={() => {
              onClose();
              resetForm();
            }}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            disabled={!formData.title || !formData.content}
            isLoading={createNoteMutation.isPending}
          >
            Create Note
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
